import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import { visuallyHidden } from "@mui/utils";
import { createStyles, makeStyles } from "@mui/styles";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from "@mui/material/CircularProgress";
import { headCells, rowsPerPageOptions } from "./data";
import AddForm from "./AddForm";

import { showToast } from "../../store/actions";
import { ConfirmPopup } from "../Common/Common";
import ProductService from "../../service/product";

const tableWidths = headCells.map((x) => x.width);

const useStyles = makeStyles((theme) =>
    createStyles({
        tr: {
            "& td, & th": {
                wordBreak: "break-word"
            },
            "&:nth-of-type(odd)": {
                backgroundColor: "#ffffff",

                "& td:first-child": {
                    backgroundColor: "#ffffff"
                }
            },
            "&:nth-of-type(even)": {
                "& td:first-child": {
                    backgroundColor: "white"
                }
            },
        },
        Header: {
            fontSize: "1.1rem",
            minHeight: "60px !important",
            backgroundColor: "#e8edf1",
            color: "black",
            marginTop: 0,
            borderRadius: 0,
            boxShadow: 0,
            fontWeight: 600
        },
        TableHead: {
            fontWeight: 600,
            "& span.Mui-active": {
                background: "transparent",
                color: "inherit"
            },
            "& span": {
                "&:hover": {
                    background: "transparent",
                    color: "rgba(0, 0, 0, 0.6) !important"
                }
            }
        }
    })
);

export default function DataTable() {
    const classes = useStyles();
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("SortOrder");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [open, setOpen] = useState(false);
    const [tableHeight, setTableHeight] = useState(0);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [Status, setStatus] = useState(-1);
    const [productId, setProductId] = useState(0);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [category, setCategory] = useState(-1);
    const [categories, setCategories] = useState([{ label: "All", value: -1 }]);

    const dispatch = useDispatch();

    const listingRef = useRef({ search, limit, page, orderBy, order, category, Status });

    const updateListingRefObj = (options) => {
        listingRef.current = { search, limit, page, orderBy, order, category, Status, ...options };
        loadProductdata(options);
    };

    // Loading Product List
    const loadProductdata = useCallback(async (options) => {
        setLoading(true);

        const productService = new ProductService();

        const result = await productService.getAllProducts({
            search: listingRef.current.search,
            limit: listingRef.current.limit,
            page: listingRef.current.page,
            orderBy: listingRef.current.orderBy,
            order: listingRef.current.order,
            category: listingRef.current.category,
            Status: listingRef.current.Status,
            ...options,
        });

        if (result.success && result.data && Object.keys(result.data.productList).length > 0) {
            setData(result.data.productList);
            setCount(result.data.count);
        } else {
            setData([]);
            setCount(0);
        }

        setLoading(false);
    }, []);

    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setPage(0);
            updateListingRefObj({ page: 0 });
        }
    };

    const handleClearSearch = () => {
        setSearch("");
        setPage(0);
        updateListingRefObj({
            search: "",
            page: 0
        });
    };

    const handleCategoryFilter = (value) => {
        setCategory(value);
        setPage(0);
        updateListingRefObj({ category: value, page: 0 });
    };

    const handleStatusFilter = (value) => {
        setStatus(value);
        setPage(0);
        updateListingRefObj({ Status: value, page: 0 });
    };

    useEffect(() => {
        (async () => {
            const productService = new ProductService();
            const response = await productService.getProductCategories();

            if (response.success) {
                // Set Category filter
                setCategories((prev) => [
                    ...prev, ...response.data
                ]);
            }
        })();

        return () => {
            setCategories([{ label: "All", value: -1 }]);
        };
    }, []);

    // Load Product list on start
    useEffect(() => {
        loadProductdata();
        return () => setData([]);
    }, [loadProductdata]);

    useLayoutEffect(() => {
        function updateSize() {
            setTableHeight(window.innerHeight - 230);
        }

        window.addEventListener("resize", updateSize);
        updateSize();

        return () => window.removeEventListener("resize", updateSize);
    }, []);

    const handleRequestSort = (_event, property) => {
        const isAsc = orderBy === property && order === "asc";
        let tmpOrder = isAsc ? "desc" : "asc";

        setOrder(tmpOrder);
        setOrderBy(property);
        updateListingRefObj({ orderBy: property, order: tmpOrder });
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
        updateListingRefObj({
            page: newPage
        });
    };

    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
        updateListingRefObj({
            limit: parseInt(event.target.value, 10),
            page: 0
        });
    };

    // Function for Delete Product
    const deleteProduct = async (productId) => {
        setLoadingDelete(true);

        const productService = new ProductService();
        const result = await productService.deleteProduct(productId);

        if (result.success) {
            loadProductdata();

            dispatch(
                showToast({
                    message: result.message,
                    type: "success"
                })
            );
        }

        setOpenDelete(false);
        setLoadingDelete(false);
    };

    function DataTableHead(props) {
        const { order, orderBy, onRequestSort, hidden, classes } = props;
        const createSortHandler = (property) => (event) => {
            onRequestSort(event, property);
        };

        return (
            <>
                <TableHead style={hidden ? { visibility: "collapse" } : {}}>
                    <TableRow>
                        {headCells.map((headCell) => (
                            <TableCell
                                key={headCell.id}
                                align={headCell.numeric ? "right" : "left"}
                                padding={headCell.disablePadding ? "none" : "normal"}
                                sortDirection={orderBy === headCell.id && !headCell.sortable ? order : false}
                                style={{
                                    width: headCell.width,
                                    ...(headCell.id === "Name" && {
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 900,
                                    }),
                                }}
                                className={classes.TableHead}
                            >
                                {!headCell.sortable ? (
                                    headCell.label
                                ) : (
                                    <TableSortLabel
                                        active={orderBy === headCell.id}
                                        direction={orderBy === headCell.id ? order : "asc"}
                                        onClick={createSortHandler(headCell.id)}
                                    >
                                        {headCell.label}
                                        {orderBy === headCell.id ? (
                                            <Box component="span" sx={visuallyHidden}>
                                                {order === "desc" ? "sorted descending" : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
            </>
        );
    }

    DataTableHead.propTypes = {
        onRequestSort: PropTypes.func.isRequired,
        order: PropTypes.oneOf(["asc", "desc"]).isRequired,
        orderBy: PropTypes.string.isRequired,
        rowCount: PropTypes.number.isRequired,
    };

    const DataTableToolbar = () => {
        const statusList = [
            { label: "All", value: -1 },
            { label: "Active", value: 1 },
            { label: "Inactive", value: 0 }
        ];

        return (
            <Toolbar
                className={classes.Header}
                sx={{
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 }
                }}
            >
                <Typography variant="h6" component="div" sx={{ flex: "1 2 100%" }}>
                    Product List
                </Typography>

                <TextField
                    margin="normal"
                    value={search}
                    onKeyPress={handleSearch}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    variant="standard"
                    InputProps={{
                        startAdornment: <SearchIcon color="primary" />,
                        ...(search && {
                            endAdornment: (
                                <IconButton onClick={handleClearSearch} size="small">
                                    <ClearIcon color="primary" />
                                </IconButton>
                            ),
                        }),
                    }}
                    sx={{ mr: "20px" }}
                />

                <TextField
                    margin="normal"
                    label="Category"
                    variant="standard"
                    value={category}
                    onChange={(e) => handleCategoryFilter(e.target.value)}
                    select
                    sx={{ mr: "20px", mt: 0, width: "200px" }}
                >
                    {categories.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    margin="normal"
                    label="Status"
                    variant="standard"
                    value={Status}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                    select
                    sx={{ mr: "20px", mt: 0, width: "200px" }}
                    className={classes.StatusDrp}
                >
                    {statusList.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>

                <Button
                    className="btn btn-primary"
                    variant="contained"
                    style={{ width: "110px", marginLeft: "auto" }}
                    endIcon={<AddIcon />}
                    onClick={() => {
                        setOpen(true);
                        setProductId(0);
                    }}
                >
                    Add
                </Button>
            </Toolbar>
        );
    };

    // Open Delete Confirm Popup
    const DeletePopup = ({ productId }) => {
        return (
            <ConfirmPopup
                configData={{
                    popupTitle: "Delete Product",
                    popupMessage: "Are you sure you want to delete?",
                    handleCancel: handleDeleteCancel,
                    handleClick: () => deleteProduct(productId),
                    loadingForConfirm: loadingDelete
                }}
            />
        );
    };

    const handleDeleteCancel = () => {
        setOpenDelete(false);
    };

    return (
        <Box>
            <AppBar
                sx={{
                    position: "relative",
                    boxShadow: 0,
                    marginTop: "15px",
                    backgroundColor: "#e8edf1"
                }}
            >
                {DataTableToolbar()}
            </AppBar>
            <Paper sx={{ width: "100%", mb: 2 }}>
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: `${tableHeight}px`
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer
                        sx={{
                            maxHeight: `${tableHeight}px`,
                            ...(data.length === 0
                                ? {
                                    overflow: "hidden",
                                }
                                : {}),
                        }}
                    >
                        <Table stickyHeader sx={{ width: "100%" }} size="small">
                            <DataTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={data.length}
                                classes={classes}
                            />
                            {data.length ? (
                                <TableBody>
                                    {data.map((row, i) => {
                                        return (
                                            <TableRow tabIndex={-1} key={row.Product_id} className={classes.tr}>
                                                <TableCell
                                                    style={{
                                                        position: "sticky",
                                                        left: 0,
                                                        zIndex: 800,
                                                        minWidth: tableWidths[0]
                                                    }}
                                                    className={classes.td}
                                                >
                                                    <span
                                                        style={{
                                                            display: "inline-block",
                                                            wordBreak: "break-word",
                                                            verticalAlign: "middle"
                                                        }}
                                                    >
                                                        {row.Name}
                                                    </span>
                                                </TableCell>
                                                <TableCell style={{ minWidth: tableWidths[1] }} component="th" scope="row" padding="normal">
                                                    {row.Category}
                                                </TableCell>
                                                <TableCell style={{ minWidth: tableWidths[2] }} component="th" scope="row" padding="normal">
                                                    <img
                                                        crossOrigin="anonymous"
                                                        alt="Product"
                                                        src={row.File ?
                                                            (process.env.REACT_APP_API_URL + '/' + row.File) :
                                                            (process.env.REACT_APP_API_URL + '/file/products/NoImageAvailable.png')
                                                        }
                                                        style={{
                                                            width: "80px",
                                                            height: "80px"
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ minWidth: tableWidths[3] }}>{row.SortOrder}</TableCell>
                                                <TableCell style={{ minWidth: tableWidths[4] }}>
                                                    {row.Status ? (
                                                        <Chip
                                                            className="status-badge badge-light-success"
                                                            label="Active"
                                                            color="success"
                                                            size="small"
                                                            variant="filled"
                                                        />
                                                    ) : (
                                                        <Chip
                                                            className="status-badge badge-light-error"
                                                            label="Inactive"
                                                            color="error"
                                                            size="small"
                                                            variant="filled"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell style={{ width: tableWidths[5] }}>
                                                    <Tooltip title="Edit Product" className="btn btn-icon btn-success-light" style={{ verticalAlign: "middle" }}>
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => {
                                                                setOpen(true);
                                                                setProductId(row.Product_id);
                                                            }}
                                                        >
                                                            <EditOutlinedIcon />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Delete Product" className="btn btn-icon btn-danger-light" style={{ verticalAlign: "middle" }}>
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => {
                                                                setProductId(row.Product_id);
                                                                setOpenDelete(true);
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            ) : (
                                <TableBody></TableBody>
                            )}
                        </Table>
                    </TableContainer>
                )}
                <>
                    {!data.length && !loading && (
                        <div
                            style={{
                                height: tableHeight - 64, //64 for the table title space
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            No Data Available
                        </div>
                    )}
                </>
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={count}
                    rowsPerPage={limit}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {open && <AddForm setOpen={setOpen} loadProductdata={loadProductdata} productId={productId} categories={categories} />}
            {openDelete && <DeletePopup productId={productId} />}
        </Box>
    );
}
